import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  // Branding
  const [banner, setBanner] = useState("#2E3192");
  const image = "/2Q9A1986.JPG"; // FINAL IMAGE – must be in /public

  const navigate = useNavigate();

  useEffect(() => {
    const storedEmail = localStorage.getItem("rememberedEmail");
    const savedColour = localStorage.getItem("loginBanner");

    if (storedEmail) {
      setEmail(storedEmail);
      setRememberMe(true);
    }

    if (savedColour) setBanner(savedColour);

    // Safety cleanup in case an old login image was stored
    localStorage.removeItem("loginImage");
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);

    const cleanEmail = email.trim();

    if (rememberMe) localStorage.setItem("rememberedEmail", cleanEmail);
    else localStorage.removeItem("rememberedEmail");

    const { data, error: signInErr } = await supabase.auth.signInWithPassword({
      email: cleanEmail,
      password,
    });

    if (signInErr) {
      setLoading(false);
      setError(signInErr.message || "Login failed.");
      return;
    }

    const userId = data?.user?.id;
    if (!userId) {
      setLoading(false);
      setError("Login succeeded but no user was returned.");
      return;
    }

    // ADDED: fetch role + active status
    const { data: profile, error: profileErr } = await supabase
      .from("profiles")
      .select("role, active")
      .eq("user_id", userId)
      .single();

    setLoading(false);

    if (profileErr) {
      navigate("/learner/dashboard");
      return;
    }

    // ADDED: block disabled users
    if (profile?.active === false) {
      await supabase.auth.signOut();
      setError("Your account has been disabled. Please contact your administrator.");
      return;
    }

    const role = (profile?.role || "learner").toLowerCase();

    if (role === "admin") navigate("/admin/users");
    else if (role === "manager") navigate("/learner/dashboard");
    else navigate("/learner/dashboard");
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");

    const cleanEmail = email.trim();
    if (!cleanEmail) {
      setError("Enter your email first, then click Forgot your password.");
      return;
    }

    setLoading(true);

    const { error: resetErr } = await supabase.auth.resetPasswordForEmail(
      cleanEmail,
      { redirectTo: "https://coop.vibelms.com.au/reset-password" }
    );

    setLoading(false);

    if (resetErr) {
      setError(resetErr.message || "Could not send reset email.");
      return;
    }

    setInfo("Password reset email sent. Check your inbox.");
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      {/* Top banner */}
      <div className="h-16 w-full" style={{ backgroundColor: banner }} />

      {/* Main layout */}
      <div className="flex flex-1 flex-col lg:flex-row">
        {/* Left image */}
        <div className="hidden lg:flex lg:w-2/5 items-center justify-center bg-white">
          <img
            src={image}
            alt="Login visual"
            className="max-w-[90%] max-h-[500px] object-contain rounded-lg shadow"
          />
        </div>

        {/* Login form */}
        <div className="flex flex-col justify-center items-center lg:w-3/5 p-8 bg-gray-50">
          <div className="w-full max-w-md space-y-6">
            <h2 className="text-2xl font-bold text-gray-800 text-center">
              Log in to My Co-op Learning
            </h2>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block mb-1 font-medium">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border rounded px-3 py-2 shadow-sm hover:shadow-md transition focus:outline-none focus:ring-2 focus:ring-orange-400"
                  required
                  autoComplete="email"
                />
              </div>

              <div>
                <label className="block mb-1 font-medium">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border rounded px-3 py-2 shadow-sm hover:shadow-md transition focus:outline-none focus:ring-2 focus:ring-orange-400"
                  required
                  autoComplete="current-password"
                />
              </div>

              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span>Remember me</span>
                </label>

                <button
                  onClick={handleForgotPassword}
                  type="button"
                  className="text-[#2E3192] hover:underline disabled:opacity-60"
                  disabled={loading}
                >
                  Forgot your password?
                </button>
              </div>

              {error && <p className="text-red-500 text-sm">{error}</p>}
              {info && <p className="text-green-600 text-sm">{info}</p>}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#8CC63F] hover:bg-[#7BB235] disabled:opacity-60 text-white py-2 rounded font-semibold shadow-md transition transform hover:scale-105 active:scale-95"
              >
                {loading ? "Logging in..." : "Log In"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
